import jsPDF from "jspdf";
import QRCode from "qrcode";
import type { TFunction } from "i18next";
import type { PkInvoice, PkPayment, PkTicket, ShiftReport } from "../api/parkingApi";

// ============================================================================
// Control de estacionamientos — PDF imprimibles (jspdf + qrcode, ya en el
// bundle): ticket de entrada y recibo de pago (80 mm, tipo impresora térmica)
// con QR, resumen del corte de caja (carta) y prefactura (carta).
//   • El QR del ticket codifica su token (lo lee la caja o la pluma de salida).
//   • El QR del recibo abre el portal de autofactura con folio e importe.
// ============================================================================

export const SELF_INVOICE_PATH = "/autofactura-estacionamiento";

/** Liga del portal con folio + código de autofactura (el importe ya no es credencial). */
export function selfInvoiceUrl(folio: string, code: string, origin = typeof window !== "undefined" ? window.location.origin : ""): string {
  const q = new URLSearchParams({ folio, code });
  return `${origin}${SELF_INVOICE_PATH}?${q.toString()}`;
}

const fmtMoney = (n: number | null | undefined, lang: string) =>
  new Intl.NumberFormat(lang.startsWith("en") ? "en-US" : "es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);
const fmtDate = (iso: string | null | undefined, lang: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(lang.startsWith("en") ? "en-US" : "es-MX", { dateStyle: "short", timeStyle: "short" });
};

async function qrDataUrl(text: string): Promise<string> {
  return QRCode.toDataURL(text, { margin: 1, width: 320 });
}

/** Ticket de entrada o recibo de pago (si trae `payment`). */
export async function buildTicketPdf({ ticket, payment = null, t, lang }: { ticket: PkTicket; payment?: PkPayment | null; t: TFunction; lang: string }): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "mm", format: [80, payment ? 190 : 150] });
  const W = 80;
  const center = (text: string, y: number, size = 9, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.text(text, W / 2, y, { align: "center" });
  };
  let y = 10;
  center(ticket.lot_name || t("pdf.parking"), y, 11, true);
  y += 5;
  center(payment ? t("pdf.receipt") : t("pdf.entryTicket"), y, 9);
  y += 6;
  doc.setDrawColor(180);
  doc.line(6, y, W - 6, y);
  y += 6;
  center(ticket.folio, y, 16, true);
  y += 7;
  const row = (label: string, value: string) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text(label, 6, y);
    doc.setTextColor(20);
    doc.setFont("helvetica", "bold");
    doc.text(value, W - 6, y, { align: "right" });
    y += 5;
  };
  row(t("pdf.plate"), ticket.plate || "—");
  row(t("pdf.entry"), fmtDate(ticket.entry_at, lang));
  if (ticket.space_code) row(t("pdf.space"), ticket.space_code);
  if (ticket.rate_name) row(t("pdf.rate"), ticket.rate_name);
  if (payment) {
    row(t("pdf.paidAt"), fmtDate(payment.paid_at || payment.createdAt, lang));
    if (ticket.amount) row(t("pdf.time"), `${ticket.amount.minutes} min`);
    for (const line of ticket.amount?.lines ?? []) {
      if (line.amount === 0 && line.concept !== "grace") continue;
      row(t(`fee.${line.concept}`), fmtMoney(line.amount, lang));
    }
    y += 1;
    doc.line(6, y, W - 6, y);
    y += 6;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(t("pdf.total"), 6, y);
    doc.text(fmtMoney(payment.amount, lang), W - 6, y, { align: "right" });
    y += 6;
    row(t("pdf.method"), t(`method.${payment.method}`));
    if (payment.method === "cash") {
      row(t("pdf.received"), fmtMoney(payment.received, lang));
      row(t("pdf.change"), fmtMoney(payment.change, lang));
    }
    if (payment.reference) row(t("pdf.reference"), payment.reference);
    if (ticket.exit_deadline) row(t("pdf.exitBefore"), fmtDate(ticket.exit_deadline, lang));
    if (ticket.invoice_key) row(t("pdf.invoiceCode"), ticket.invoice_key);
  }
  y += 2;
  const qrText = payment && ticket.invoice_key ? selfInvoiceUrl(ticket.folio, ticket.invoice_key) : ticket.qr_token;
  const size = 38;
  doc.addImage(await qrDataUrl(qrText), "PNG", (W - size) / 2, y, size, size);
  y += size + 4;
  doc.setTextColor(90);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  const note = payment ? t("pdf.invoiceHint") : t("pdf.keepTicket");
  doc.text(doc.splitTextToSize(note, W - 12), W / 2, y, { align: "center" });
  return doc;
}

/** Resumen imprimible del corte de caja. */
export function buildShiftPdf({ report, t, lang }: { report: ShiftReport; t: TFunction; lang: string }): jsPDF {
  const { shift, payments, totals } = report;
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  let y = 18;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(`${t("pdf.shiftReport")} #${shift.number}`, 15, y);
  y += 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`${shift.lot_name ?? ""} · ${shift.cashier_name ?? ""}`, 15, y);
  y += 5;
  doc.text(`${fmtDate(shift.opened_at, lang)} → ${shift.closed_at ? fmtDate(shift.closed_at, lang) : t("shifts.status.open")}`, 15, y);
  y += 9;
  const kv = (label: string, value: string, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.text(label, 15, y);
    doc.text(value, 110, y, { align: "right" });
    y += 6;
  };
  kv(t("shifts.openingFloat"), fmtMoney(shift.opening_float, lang));
  for (const m of ["cash", "card_terminal", "stripe", "link", "free"] as const) kv(t(`method.${m}`), fmtMoney(totals.by_method?.[m], lang));
  kv(t("shifts.total"), fmtMoney(totals.total, lang), true);
  kv(t("shifts.expectedCash"), fmtMoney(totals.expected_cash, lang), true);
  if (shift.counted_cash !== undefined) {
    kv(t("shifts.countedCash"), fmtMoney(shift.counted_cash, lang));
    kv(t("shifts.difference"), fmtMoney(shift.difference, lang), true);
  }
  kv(t("shifts.payments"), String(totals.count));
  y += 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const cols = [15, 45, 85, 125, 160];
  [t("pdf.paidAt"), t("pdf.folio"), t("pdf.plate"), t("pdf.method"), t("pdf.total")].forEach((h, i) => doc.text(h, cols[i], y));
  y += 2;
  doc.line(15, y, 200, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  for (const p of payments) {
    if (y > 265) {
      doc.addPage();
      y = 18;
    }
    doc.text(fmtDate(p.paid_at || p.createdAt, lang), cols[0], y);
    doc.text(p.folio || (p.holder_name ? `${t("pdf.pension")} · ${p.holder_name}`.slice(0, 26) : "—"), cols[1], y);
    doc.text(p.plate || "—", cols[2], y);
    doc.text(t(`method.${p.method}`), cols[3], y);
    doc.text(fmtMoney(p.amount, lang), 200, y, { align: "right" });
    y += 5;
  }
  y += 14;
  doc.line(25, y, 85, y);
  doc.line(125, y, 185, y);
  y += 5;
  doc.text(t("pdf.cashierSignature"), 55, y, { align: "center" });
  doc.text(t("pdf.supervisorSignature"), 155, y, { align: "center" });
  return doc;
}

/** Prefactura (sin valor fiscal) de una solicitud lista para timbrar. */
export function buildPrefacturaPdf({ invoice, t, lang }: { invoice: PkInvoice; t: TFunction; lang: string }): jsPDF {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  let y = 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(t("pdf.prefactura"), 15, y);
  doc.setFontSize(11);
  doc.text(invoice.prefactura_number || "—", 200, y, { align: "right" });
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(180, 60, 40);
  doc.text(t("pdf.noFiscalValue"), 15, y);
  doc.setTextColor(20);
  y += 10;
  const kv = (label: string, value: string) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(110);
    doc.text(label, 15, y);
    doc.setTextColor(20);
    doc.setFont("helvetica", "bold");
    doc.text(value, 70, y);
    y += 6;
  };
  kv(t("invoices.rfc"), invoice.rfc);
  kv(t("invoices.legalName"), invoice.legal_name);
  kv(t("invoices.taxRegime"), `${invoice.tax_regime} — ${t(`regime.${invoice.tax_regime}`)}`);
  kv(t("invoices.cfdiUse"), `${invoice.cfdi_use} — ${t(`cfdiUse.${invoice.cfdi_use}`)}`);
  kv(t("invoices.zip"), invoice.zip);
  kv(t("invoices.email"), invoice.email);
  kv(t("pdf.folio"), invoice.folio);
  kv(t("pdf.paidAt"), fmtDate(invoice.paid_at, lang));
  y += 4;
  doc.setFont("helvetica", "bold");
  const cols = [15, 40, 60, 160];
  [t("pdf.satKey"), t("pdf.unit"), t("pdf.description"), t("pdf.amount")].forEach((h, i) => doc.text(h, cols[i], y));
  y += 2;
  doc.line(15, y, 200, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  const c = invoice.concept;
  doc.text(c?.product_key || "78111807", cols[0], y);
  doc.text(c?.unit_key || "E48", cols[1], y);
  doc.text(doc.splitTextToSize(c?.description || t("pdf.parkingService"), 95), cols[2], y);
  doc.text(fmtMoney(invoice.subtotal, lang), 200, y, { align: "right" });
  y += 16;
  const tot = (label: string, value: number, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.text(label, 150, y);
    doc.text(fmtMoney(value, lang), 200, y, { align: "right" });
    y += 6;
  };
  tot(t("pdf.subtotal"), invoice.subtotal);
  tot(t("pdf.iva"), invoice.iva);
  tot(t("pdf.total"), invoice.total, true);
  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(110);
  doc.text(doc.splitTextToSize(t("pdf.prefacturaNote"), 185), 15, y);
  return doc;
}

/** Abre el PDF en una pestaña (o lo descarga si el navegador lo bloquea). */
export function openPdf(doc: jsPDF, filename: string) {
  const url = doc.output("bloburl");
  const win = window.open(String(url), "_blank", "noopener");
  if (!win) doc.save(filename);
}
